// Models
import { DependencyType } from "../service/planner/enumerations/IPlannerEnumerations";
/**
 * Validates summary-task-specific rules: rejects ancestor-descendant dependencies
 * and restricts constraints to FS or SS.
 * @param dependency - Candidate dependency edge.
 * @param sourceTask - Source task of the dependency.
 * @param targetTask - Target task of the dependency.
 * @param allTasksMap - All tasks in the plan, keyed by task id.
 */ export function validateSummaryTaskDependency(dependency, sourceTask, targetTask, allTasksMap) {
    if (isAncestorOf(sourceTask, targetTask, allTasksMap) || isAncestorOf(targetTask, sourceTask, allTasksMap)) {
        return false;
    }
    return dependency.constraint === DependencyType.SourceFinishDrivesTargetStart || dependency.constraint === DependencyType.SourceStartDrivesTargetStart;
}
/**
 * Returns true when `ancestorTask` is an ancestor of `task` by walking up the parentTaskId chain.
 * @param task - The potential descendant task.
 * @param ancestorTask - The potential ancestor task.
 * @param allTasksMap - All tasks in the plan, keyed by task id.
 */ export function isAncestorOf(task, ancestorTask, allTasksMap) {
    let currentId = task.parentTaskId;
    while(currentId != null){
        if (currentId === ancestorTask.id) {
            return true;
        }
        currentId = allTasksMap.get(currentId)?.parentTaskId ?? null;
    }
    return false;
}
/**
 * Runs all dependency-level validations. When isBeingCreated is true, also checks
 * the plan dependency limit and validates that adding the candidate does not introduce a cycle.
 * @param dependency - Candidate dependency.
 * @param planDependencies - Existing dependencies in the plan.
 * @param allTasksMap - All tasks in the plan, keyed by task id.
 * @param isBeingCreated - True when the candidate has not yet been added to the plan dependencies.
 * @param dependencySettings - Configurable dependency settings.
 */ export function validateDependency(dependency, planDependencies, allTasksMap, isBeingCreated, dependencySettings) {
    let isDependencyValid = dependency.sourceItemId !== dependency.targetItemId; // Reject self-dependencies
    // Reject if either task has exceeded the per-task dependency limit
    isDependencyValid &&= isWithinTaskDependencyLimit(allTasksMap.get(dependency.sourceItemId), isBeingCreated, dependencySettings.taskDependencyLimit);
    isDependencyValid &&= isWithinTaskDependencyLimit(allTasksMap.get(dependency.targetItemId), isBeingCreated, dependencySettings.taskDependencyLimit);
    if (isBeingCreated) {
        isDependencyValid &&= planDependencies.length < dependencySettings.planDependencyLimit;
        isDependencyValid &&= !hasCycle([
            ...planDependencies,
            dependency
        ]);
    }
    const sourceTask = allTasksMap.get(dependency.sourceItemId);
    const targetTask = allTasksMap.get(dependency.targetItemId);
    const isSummaryTask = sourceTask?.hasChildren === true || targetTask?.hasChildren === true;
    if (isSummaryTask && sourceTask && targetTask) {
        isDependencyValid &&= validateSummaryTaskDependency(dependency, sourceTask, targetTask, allTasksMap);
    }
    return isDependencyValid;
}
/**
 * Validates the plan-wide dependency limit and structural rules.
 * Checks for cycles once across the full graph, then validates
 * summary task rules per dependency.
 * @param planDependencies - All dependencies in the plan.
 * @param allTasksMap - All tasks in the plan, keyed by task id.
 * @param dependencySettings - Configurable dependency settings.
 */ export function validatePlanDependencies(planDependencies, allTasksMap, dependencySettings) {
    let isValid = planDependencies.length <= dependencySettings.planDependencyLimit;
    isValid &&= !hasCycle(planDependencies);
    isValid &&= planDependencies.every((dep)=>validateDependency(dep, planDependencies, allTasksMap, /* isBeingCreated */ false, dependencySettings));
    return isValid;
}
/**
 * Checks whether a task is within the per-task dependency limit.
 * Uses strict inequality (<) when creating to prevent adding beyond the limit,
 * and non-strict (<=) otherwise to allow existing tasks at exactly the limit.
 * @param task - The task to check, or undefined if not found.
 * @param isBeingCreated - True when the candidate dependency has not yet been added.
 * @param taskDependencyLimit - Maximum number of dependencies allowed per task.
 */ function isWithinTaskDependencyLimit(task, isBeingCreated, taskDependencyLimit) {
    const count = task?.dependencies.length ?? 0;
    return isBeingCreated ? count < taskDependencyLimit : count <= taskDependencyLimit;
}
/**
 * Returns true when the dependency graph contains a cycle.
 * Each task is treated as a single node with edges from sourceItemId to targetItemId.
 * Uses DFS with visited + path-visited tracking to detect back edges.
 * @param dependencies - Dependencies to check.
 */ export function hasCycle(dependencies) {
    const adjacency = new Map();
    for (const dep of dependencies){
        const neighbors = adjacency.get(dep.sourceItemId) ?? new Set();
        neighbors.add(dep.targetItemId);
        adjacency.set(dep.sourceItemId, neighbors);
        if (!adjacency.has(dep.targetItemId)) {
            adjacency.set(dep.targetItemId, new Set());
        }
    }
    const visited = new Set();
    const pathVisited = new Set();
    // DFS to detect cycles
    const traverseForCycle = (node)=>{
        visited.add(node);
        pathVisited.add(node);
        for (const neighbor of adjacency.get(node)){
            if (!visited.has(neighbor)) {
                if (traverseForCycle(neighbor)) {
                    return true;
                }
            } else if (pathVisited.has(neighbor)) {
                return true;
            }
        }
        pathVisited.delete(node);
        return false;
    };
    for (const node of adjacency.keys()){
        if (!visited.has(node)) {
            if (traverseForCycle(node)) {
                return true;
            }
        }
    }
    return false;
}
